import { addSentryBreadcrumb } from "JS/Helpers/SentryHelper";
import {
    DisclosureDownloadReason,
    DisclosurePlacement,
    DisclosurePlayEvent,
    DisclosurePlayReason,
} from "JS/Models/Disclosure";
import { ContentType } from "JS/Models/Media";
import {
    EventActions,
    EventCategories,
    EventNames,
    FirebaseEventAction,
} from "JS/Models/Firebase/Events";
import { courseFirebaseService } from "JS/Services/Firebase";

export type DisclosureEventSender = (
    eventName: EventNames,
    action: FirebaseEventAction,
) => void;

const isOnline = () => globalThis.navigator?.onLine !== false;

export const disclosurePlayedPayload = (
    event: DisclosurePlayEvent,
): FirebaseEventAction => ({
    action: EventActions.DISCLOSURE_PLAYED,
    category: EventCategories.DISCLOSURES,
    skuId: event.skuId,
    nId: event.nId,
    disclosureId: event.disclosureId,
    placement: event.placement,
    contentType: event.contentType,
});

export const disclosurePlayFailedPayload = (
    event: DisclosurePlayEvent & { reason: DisclosurePlayReason },
): FirebaseEventAction => ({
    ...disclosurePlayedPayload(event),
    action: EventActions.DISCLOSURE_PLAY_FAILED,
    reason: event.reason,
});

export interface DisclosureDownloadContext {
    disclosureId: string;
    contentType?: ContentType;
    placement?: DisclosurePlacement;
    skuId?: string;
}

export const disclosureDownloadPayload = (
    action: EventActions,
    context: DisclosureDownloadContext,
    reason?: DisclosureDownloadReason,
): FirebaseEventAction => {
    const payload: FirebaseEventAction = {
        action,
        category: EventCategories.DISCLOSURES,
        disclosureId: context.disclosureId,
    };

    if (context.contentType) payload.contentType = context.contentType;
    if (context.placement) payload.placement = context.placement;
    if (context.skuId) payload.skuId = context.skuId;
    if (reason) payload.reason = reason;

    return payload;
};

export const DISCLOSURE_FETCH_ERROR = "DisclosureFetchError";

const STORAGE_ERROR_NAMES = [
    "AbortError",
    "ConstraintError",
    "DataError",
    "InvalidStateError",
    "UnknownError",
];

const errorName = (error: unknown) => (error as { name?: string })?.name ?? "";

export const playFailureReason = (error: unknown): DisclosurePlayReason =>
    errorName(error) === "TypeError"
        ? "networkUnavailable"
        : "signedUrlRequestFailed";

export const downloadFailureReason = (
    error: unknown,
): DisclosureDownloadReason => {
    const name = errorName(error);

    if (name === "QuotaExceededError") return "storageFailed";
    if (name.includes("Dexie") || STORAGE_ERROR_NAMES.includes(name))
        return "storageFailed";

    // the response came back, it just was not ok
    if (name === DISCLOSURE_FETCH_ERROR) return "httpError";
    // fetch rejects with TypeError for a dropped connection and for CORS alike
    if (name === "TypeError") return "networkUnavailable";

    return "signedUrlRequestFailed";
};

const payloadFor = (event: DisclosurePlayEvent) =>
    event.reason
        ? disclosurePlayFailedPayload({
              ...event,
              reason: event.reason,
          })
        : disclosurePlayedPayload(event);

// Analytics are online only, as they are everywhere else in the app: Firebase
// accepts nothing without a connection, and a play made offline is not reported.
const recordDisclosureEvent = (
    eventName: EventNames,
    event: DisclosurePlayEvent,
    send: DisclosureEventSender,
) => {
    try {
        if (!isOnline() || !courseFirebaseService.isAnalyticsReady()) return;

        send(eventName, payloadFor(event));
    } catch {
        addSentryBreadcrumb("disclosure play record failed", {
            placement: event?.placement ?? "unknown",
        });
    }
};

export const recordDisclosurePlayed = (
    event: DisclosurePlayEvent,
    send: DisclosureEventSender,
) => recordDisclosureEvent(EventNames.DISCLOSURE_PLAYED, event, send);

export const recordDisclosurePlayFailed = (
    event: DisclosurePlayEvent & { reason: DisclosurePlayReason },
    send: DisclosureEventSender,
) => recordDisclosureEvent(EventNames.DISCLOSURE_PLAY_FAILED, event, send);

const logDisclosureDownload = (
    eventName: EventNames,
    action: EventActions,
    context: DisclosureDownloadContext,
    reason?: DisclosureDownloadReason,
) => {
    try {
        courseFirebaseService.init();
        courseFirebaseService.logFirebaseEvent(
            eventName,
            disclosureDownloadPayload(action, context, reason),
        );
    } catch {
        addSentryBreadcrumb("disclosure download event failed", {
            disclosureId: context.disclosureId,
        });
    }
};

export const logDisclosureDownloadStarted = (
    context: DisclosureDownloadContext,
) =>
    logDisclosureDownload(
        EventNames.DISCLOSURE_DOWNLOAD_STARTED,
        EventActions.DISCLOSURE_DOWNLOAD_STARTED,
        context,
    );

export const logDisclosureDownloadFinished = (
    context: DisclosureDownloadContext,
) =>
    logDisclosureDownload(
        EventNames.DISCLOSURE_DOWNLOAD_FINISHED,
        EventActions.DISCLOSURE_DOWNLOAD_FINISHED,
        context,
    );

export const logDisclosureDownloadFailed = (
    context: DisclosureDownloadContext,
    reason: DisclosureDownloadReason,
) =>
    logDisclosureDownload(
        EventNames.DISCLOSURE_DOWNLOAD_FAILED,
        EventActions.DISCLOSURE_DOWNLOAD_FAILED,
        context,
        reason,
    );
